import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

export const usePWA = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [canInstallPrompt, setCanInstallPrompt] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if app is installed
    const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                          (window.navigator as any).standalone === true;
    setIsInstalled(isAppInstalled);

    // If not installed, show install prompt after user engagement
    if (!isAppInstalled) {
      // Check if user has engaged with the page (clicked/spent time)
      const checkInstallability = () => {
        const isHTTPS = location.protocol === 'https:' || location.hostname === 'localhost';
        const hasServiceWorker = 'serviceWorker' in navigator;
        const hasManifest = document.querySelector('link[rel="manifest"]');
        
        if (isHTTPS && hasServiceWorker && hasManifest) {
          console.log('PWA is installable - showing prompt');
          // Mostrar el botón de instalación siempre que no esté instalada
          // En iOS y otros navegadores que no soportan beforeinstallprompt,
          // mostraremos las instrucciones manuales
          setIsInstallable(true);
        }
      };

      // Check installability after user interaction
      const handleUserEngagement = () => {
        setTimeout(() => {
          checkInstallability();
        }, 1000); // Small delay to ensure page is ready
      };

      document.addEventListener('click', handleUserEngagement, { once: true });
      
      // Also check after some time if user hasn't clicked
      setTimeout(checkInstallability, 3000);
    }

    // Listen for install prompt
    // Chrome/Edge fire this event
    type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      console.log('beforeinstallprompt event triggered');
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstallPrompt(true);
      setIsInstallable(true);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      console.log('App installed successfully');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      toast({
        title: "¡App instalada!",
        description: "Viabiliza se ha instalado correctamente en tu dispositivo",
      });
    };

    // Service worker registration
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((reg) => {
          setRegistration(reg);
          
          // Check for updates
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  console.log('Nueva actualización detectada');
                  setUpdateAvailable(true);
                }
              });
            }
          });

          // Check immediately if there's a waiting worker
          if (reg.waiting) {
            console.log('Service worker esperando actualización');
            setUpdateAvailable(true);
          }

          // Check for updates every 30 seconds
          setInterval(() => {
            reg.update();
          }, 30000);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [toast]);

  const installApp = async () => {
    console.log('Install button clicked, deferredPrompt:', !!deferredPrompt);
    
    if (deferredPrompt) {
      // Use native install prompt if available
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log('User choice outcome:', outcome);
        
        if (outcome === 'accepted') {
          setIsInstallable(false);
          toast({
            title: "Instalando...",
            description: "La aplicación se está instalando",
          });
        } else {
          toast({
            title: "Instalación cancelada",
            description: "Puedes instalar la app más tarde desde el menú del navegador",
          });
          setIsInstallable(false);
        }
        
        setCanInstallPrompt(false);
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Error during installation:', error);
        showManualInstallInstructions();
      }
    } else {
      toast({
        title: 'Instalación no disponible',
        description: 'Tu navegador no permite la instalación directa.',
        duration: 6000,
      });
    }
  };

  const showManualInstallInstructions = () => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isChrome = /Chrome/i.test(navigator.userAgent);
    const isSafari = /Safari/i.test(navigator.userAgent) && !isChrome;

    let instructions = "Ve al menú del navegador y selecciona 'Instalar aplicación'";
    
    if (isAndroid && isChrome) {
      instructions = "Toca el menú ⋮ de Chrome y selecciona 'Instalar aplicación'";
    } else if (isIOS && isSafari) {
      instructions = "Toca el botón Compartir 📤 y selecciona 'Añadir a pantalla de inicio'";
    } else if (isChrome) {
      instructions = "Haz clic en el menú ⋮ de Chrome y selecciona 'Instalar Viabiliza'";
    }

    toast({
      title: "Instalar Viabiliza",
      description: instructions,
      duration: 8000,
    });
  };

  const updateApp = () => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  return {
    isInstallable,
    isInstalled,
    updateAvailable,
    canInstallPrompt,
    installApp,
    updateApp
  };
};